/**
 * RiskPinLayer — Forest Sentry property risk-pin overlay.
 *
 * Renders the entitled property risk pins as native Leaflet markers on the map
 * canvas. Pins are PER-HAZARD (a stand with two concurrent advisories renders
 * two markers, one per hazard) — never worst-hazard-only. Severity colour is
 * SEMANTIC ONLY and resolved through the governed map-swatch tokens
 * (RiskPinLayer.module.css, parity with mapLegendPalette low/moderate/
 * critical), keyed by the canonical HazardSeverity ramp (elevated/high/severe)
 * so SEVERE renders visibly distinct from HIGH. Clicking a pin opens the
 * closed-by-default right inspector for that stand via the same handler as a
 * stand-click, so the inspector's real /hazards path drives the detail (AC3).
 *
 * The overlay is only ever fed by the live property hazard report. Unavailable
 * coverage stays unavailable; the canvas never fabricates risk pins.
 */
import { useMemo } from "react";
import { Marker, Popup } from "react-leaflet";
import * as L from "leaflet";
import styles from "./RiskPinLayer.module.css";
import { useLanguage } from "@/i18n";
import type {
  ActiveHazardSeverity,
  PropertyRiskPin,
} from "@/api/propertyHazardReport";
import {
  riskFeatureId,
  riskHazardLabel,
  riskLabelCopy,
  riskSeverityLabel,
} from "@/utils/sentryRiskAdvisory";

const SEVERITY_CLASS: Record<ActiveHazardSeverity, string> = {
  elevated: styles.pinElevated,
  high: styles.pinHigh,
  severe: styles.pinSevere,
};

export interface RiskPinLayerProps {
  pins: PropertyRiskPin[];
  propertyId: string;
  /** Opens the right inspector for the clicked pinned stand (real /hazards). */
  onPinClick: (standId: string, pin: PropertyRiskPin) => void;
  /** Optional stable risk-feature identity selected by a linked ranked row. */
  selectedFeatureId?: string;
  /** Stand selection used by the primary map's stand inspector. */
  selectedStandId?: string;
}

/** Build a token-styled divIcon carrying a real-DOM data-testid for E2E. */
function buildPinIcon(pin: PropertyRiskPin, selected: boolean): L.DivIcon {
  const featureId = riskFeatureId(pin);
  const classes = [
    styles.pin,
    SEVERITY_CLASS[pin.severity],
    selected ? styles.pinSelected : "",
  ]
    .filter(Boolean)
    .join(" ");
  return L.divIcon({
    className: "",
    html: `<span class="${classes}" data-fm-map-layer="sentry-risks" data-fm-map-feature-id="${featureId}" data-testid="risk-pin-${featureId}" data-severity="${pin.severity}" data-provenance="live"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

const RiskPinLayer = ({
  pins,
  propertyId,
  onPinClick,
  selectedFeatureId,
  selectedStandId,
}: RiskPinLayerProps) => {
  const { t } = useLanguage();
  const pinCopy = t.common.sentryRisk.pin;
  const labels = riskLabelCopy(t.common);

  // Memoize icons so the leaflet markers do not re-create on every render.
  const icons = useMemo(
    () =>
      pins.map((pin) =>
        buildPinIcon(
          pin,
          selectedFeatureId
            ? riskFeatureId(pin) === selectedFeatureId
            : pin.standId === selectedStandId,
        ),
      ),
    [pins, selectedFeatureId, selectedStandId],
  );

  if (pins.length === 0) return null;

  return (
    <>
      {pins.map((pin, index) => {
        const hazardLabel = riskHazardLabel(pin.hazardType, labels);
        const severityLabel = riskSeverityLabel(pin.severity, labels);
        return (
          <Marker
            key={riskFeatureId(pin)}
            position={[pin.lat, pin.lng]}
            icon={icons[index]}
            eventHandlers={{ click: () => onPinClick(pin.standId, pin) }}
          >
            <Popup className="risk-pin-popup-leaflet">
              <section className="risk-pin-popup" aria-label={hazardLabel}>
                <p className="risk-pin-popup__row">
                  {pinCopy.hazardLabel}: {hazardLabel}
                </p>
                <p className="risk-pin-popup__row">
                  {pinCopy.severityLabel}: {severityLabel}
                </p>
                <p className="risk-pin-popup__row">
                  {pinCopy.standLabel}:{" "}
                  {pin.standId.replace(`${propertyId}-`, "")}
                </p>
              </section>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

export default RiskPinLayer;
